import type { MergedRewardData } from '@/schemas/v2/reward'
import { useTranslation } from 'react-i18next'

/** 特典の獲得ステータス値 */
export type AcquisitionStatus = NonNullable<MergedRewardData['acquisitionStatus']>

type I18nLabelKey =
  | 'prize.label_unachieved'
  | 'prize.label_achieved'
  | 'prize.label_completed'
  | 'prize.label_ended'
  | 'prize.label_unavailable'

const STATUS_LABEL_KEYS: Record<AcquisitionStatus, I18nLabelKey> = {
  UNOBTAINED: 'prize.label_unachieved',
  ACHIEVABLE: 'prize.label_achieved',
  ACHIEVED: 'prize.label_achieved',
  EXCHANGED: 'prize.label_completed',
  USED: 'prize.label_completed',
  UNAPPLIED: 'prize.label_achieved',
  APPLIED: 'prize.label_completed',
  LOST: 'prize.label_completed',
  ENDED: 'prize.label_ended',
  SOLD_OUT: 'prize.label_ended',
  // SELECT_ONE グループのブロックで二度と獲得できない状態（#3495）。
  // ブロック要因は「他特典の獲得」だけでなく再挑戦不可の抽選ハズレもあり得るため、
  // 要因を断定しない中立な文言（獲得不可）にする。
  UNAVAILABLE: 'prize.label_unavailable',
}

const STATUS_COLORS: Record<AcquisitionStatus, string> = {
  UNOBTAINED: 'bg-gray-100 text-gray-500',
  ACHIEVABLE: 'bg-orange-100 text-orange-600',
  ACHIEVED: 'bg-orange-100 text-orange-600',
  EXCHANGED: 'bg-gray-100 text-gray-500',
  USED: 'bg-gray-100 text-gray-500',
  UNAPPLIED: 'bg-orange-100 text-orange-600',
  APPLIED: 'bg-gray-100 text-gray-500',
  LOST: 'bg-gray-100 text-gray-500',
  ENDED: 'bg-gray-100 text-gray-500',
  SOLD_OUT: 'bg-gray-100 text-gray-500',
  // 獲得不可は終了系と同じグレー（#3495）
  UNAVAILABLE: 'bg-gray-100 text-gray-500',
}

type Props = {
  /** 獲得ステータス。null / undefined の場合はバッジ非表示 */
  status?: AcquisitionStatus | null
  /**
   * true のとき、status に関わらず「完了」（グレー）を強制表示する。
   * ACHIEVED + 抽選型 + prizes 全ハズレ、または LOST の場合に使用する。
   */
  showLotteryDoneLabel?: boolean
}

/**
 * 特典の獲得状況バッジ。表示ラベルは「未達成 / 達成 / 完了 / 終了 / 獲得不可」の5種類。
 * 一覧・詳細の両方で共通して使用する。
 *
 * @example
 * // 通常ケース
 * <RewardAcquisitionStatusBadge status="ACHIEVABLE" />
 *
 * // 完了ラベルを強制表示（抽選ハズレなど）
 * <RewardAcquisitionStatusBadge status="ACHIEVED" showLotteryDoneLabel />
 */
export function RewardAcquisitionStatusBadge({ status, showLotteryDoneLabel = false }: Props) {
  const { t } = useTranslation()

  if (showLotteryDoneLabel) {
    return (
      <span className="inline-block rounded-full px-[8px] py-[2px] text-[12px] font-bold leading-[16px] bg-gray-100 text-gray-500">
        {t('prize.label_completed')}
      </span>
    )
  }

  if (!status) return null

  return (
    <span
      className={`inline-block rounded-full px-[8px] py-[2px] text-[12px] font-bold leading-[16px] ${STATUS_COLORS[status]}`}
    >
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  )
}
